import { Bracket, Seed } from "react-brackets";
import { useState } from "react";

import cx from "classnames";
import { useBracketContext } from "./Context";
import { logos, useGetRounds } from "./hooks";
import { RoundTitle } from "./components";

import Link from "next/link";
import { ConnectButton } from "../Actions";

const getBtnClasses = ({
  noTeam,
  winner,
  userSelectedTeam
}: {
  noTeam?: boolean;
  winner: boolean;
  userSelectedTeam: boolean;
}) =>
  cx(
    "bg-coinage-white p-2 rounded-xl border-[1.5px] flex flex-row items-center gap-4 px-4 relative",
    {
      "border-coinage-gray": userSelectedTeam && !winner,
      "marker winner border-coinage-teal": winner,
      "justify-center": noTeam
    }
  );

const CustomSeed = (props: any) => {
  const { selectedWinners, matchups, bustedIds } = useBracketContext();
  const { seed, breakpoint } = props;
  const currentGameId = seed.id;

  const homeTeamFromCms = seed.teams[0];
  const awayTeamFromCms = seed.teams[1];

  const homeMatchup = matchups[currentGameId]?.home;
  const awayMatchup = matchups[currentGameId]?.away;

  const homeTeam = homeTeamFromCms;
  const awayTeam = awayTeamFromCms;

  const HomeLogo = logos[homeTeam?.teamId] ?? logos["default"];
  const AwayLogo = logos[awayTeam?.teamId] ?? logos["default"];

  const championshipTeam =
    seed?.winner?.teamName || selectedWinners["final"]?.teamName;

  const getTitleClassName = (teamId: string) =>
    cx("text-coinage-black", {
      subhead3: teamId !== "eas",
      "caption !font-bold": teamId === "eas"
    });

  const Div = ({ href, ...rest }: any) => <div {...rest} />;

  const Wrapper = seed.url ? Link : Div;

  const date = new Date(seed.date);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

  const getteamClasses = (busted: boolean) =>
    cx("text-center", {
      "line-through	text-coinage-orange": busted,
      "text-coinage-black": !busted
    });

  return (
    <Seed mobileBreakpoint={breakpoint} className="flex 1">
      <Wrapper
        target="_blank"
        href={seed.url}
        className={cx(
          "flex flex-col gap-4 border-[1px] border-b-[2px] rounded-md border-coinage-teal p-4 w-full transition-shadow duration-300 shadow-none",
          { "hover:shadow-lg": seed.url }
        )}>
        {seed.date && (
          <span className="text-center">
            {new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
              timeZone: "America/New_York"
            }).format(date)}
          </span>
        )}

        {homeMatchup?.teamName && (
          <span
            className={getteamClasses(bustedIds.includes(homeMatchup?.teamId))}>
            {homeMatchup?.teamName}
          </span>
        )}

        <div
          className={getBtnClasses({
            noTeam: !homeTeam?.teamId,
            winner:
              seed?.winningTeam?.teamId &&
              homeTeam?.teamId === seed?.winningTeam?.teamId,
            userSelectedTeam:
              homeTeam &&
              homeTeam?.teamId === selectedWinners[currentGameId]?.teamId
          })}>
          {homeTeam?.teamId && <HomeLogo className="h-6" />}
          <span className={getTitleClassName(homeTeam?.teamId)}>
            {homeTeam?.teamName ?? "?"}
          </span>
          {seed.homeScore && (
            <span className={"caption ml-auto mr-1"}>{seed.homeScore}</span>
          )}
        </div>
        <div
          className={getBtnClasses({
            noTeam: !awayTeam?.teamId,
            winner:
              seed?.winningTeam?.teamId &&
              awayTeam?.teamId === seed?.winningTeam?.teamId,
            userSelectedTeam:
              awayTeam &&
              awayTeam?.teamId === selectedWinners[currentGameId]?.teamId
          })}>
          {awayTeam?.teamId && <AwayLogo className="h-6" />}
          <span className={getTitleClassName(awayTeam?.teamId)}>
            {awayTeam?.teamName ?? "?"}
          </span>
          {seed.awayScore && (
            <span className={"caption ml-auto mr-1"}>{seed.awayScore}</span>
          )}
        </div>
        {awayMatchup?.teamName && (
          <span
            className={getteamClasses(bustedIds.includes(awayMatchup?.teamId))}>
            {awayMatchup?.teamName}
          </span>
        )}
      </Wrapper>
      {seed.id === "final" && championshipTeam && (
        <div className={cx("transition-all duration-300 opacity-100")}>
          <div className="flex justify-between">
            <span className="ml-[25px] w-[2px] h-[25px] bg-coinage-orange"></span>
            <span className="mr-[25px] w-[2px] h-[25px] bg-coinage-orange"></span>
          </div>
          <div className="bg-coinage-orange text-coinage-white px-4 py-3 rounded-xl">
            <span className="subhead3">
              🏆&nbsp;&nbsp;&nbsp;
              {championshipTeam}
              &nbsp;&nbsp;&nbsp;🏆
            </span>
          </div>
        </div>
      )}
    </Seed>
  );
};

export default function App() {
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabIndexChange = (index: number) => () => {
    setTabIndex(index);
  };

  const handleSwipeChange = (index: number) => {
    setTabIndex(index);
  };

  const getTabClasses = (isActive: boolean) =>
    cx("py-2 border-t-4 transition-all duration-300", {
      "border-coinage-teal [&>p]:text-coinage-teal": isActive,
      "text-[#B4B4B4] border-transparent": !isActive
    });

  return (
    <>
      <section className="flex items-center w-full justify-center pb-4 flex-col bg-coinage-offWhite">
        <div className="relative w-full max-w-[1200px] mt-4">
          <div className="flex items-center w-full justify-center">
            <div className="flex flex-col items-center gap-2 bg-white py-8 w-full max-w-[1200px] rounded-t-[40px]">
              <h3 className="subhead1">Select Crypto’s Project of the Year</h3>
              <p className="body1 max-w-[75ch] text-center">
                A community-led celebration of 16 top projects in crypto, as
                voted on by you, to crown Coinage’s Crypto Project of the Year.
                Coinage NFT holders can fill out a bracket to win 0.25 ETH!
              </p>
            </div>
          </div>
          <div className="bracketTabs:hidden flex flex-row gap-2 w-full items-center sticky top-0 bg-white z-50">
            <button className="flex-1" onClick={handleTabIndexChange(0)}>
              <RoundTitle
                className={getTabClasses(tabIndex === 0)}
                title="Round 1"
                date="Nov 28th - Dec 7th"
              />
            </button>
            <button className="flex-1" onClick={handleTabIndexChange(1)}>
              <RoundTitle
                title="Round 2"
                date="Dec 12th - Dec 22nd"
                className={getTabClasses(tabIndex === 1)}
              />
            </button>
            <button className="flex-1" onClick={handleTabIndexChange(2)}>
              <RoundTitle
                className={getTabClasses(tabIndex === 2)}
                title="Round 3"
                date="Jan 3rd - Jan 4th"
              />
            </button>
            <button className="flex-1" onClick={handleTabIndexChange(3)}>
              <RoundTitle
                className={getTabClasses(tabIndex === 3)}
                title="Final"
                date="Jan 9th"
              />
            </button>
          </div>
          <Bracket
            roundClassName="!flex-1"
            id="bracket"
            style={{ border: "2px solid black" }}
            className="flex-1"
            roundTitleComponent={title => {
              return <>{title}</>;
            }}
            //@ts-expect-error
            rounds={useGetRounds()}
            renderSeedComponent={CustomSeed}
            swipeableProps={{
              enableMouseEvents: true,
              animateHeight: true,
              index: tabIndex,
              onChangeIndex: handleSwipeChange
            }}
          />
        </div>
        {/* <div className="w-full flex items-center justify-center sticky bottom-[40px]">
          <ConnectButton
            hideWhenConnected
            buttonText="Connect Wallet to Show Picks"
          />
        </div> */}
      </section>
    </>
  );
}
